import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from "react-router-dom";
import { useAuth } from "context/Auth";
import stripeService from 'services/stripe';
import LoadingOverlay from "components/LoadingOverlay";
import UpdatePaymentMethodModal from 'components/UpdatePaymentMethodModal';
import { CheckCircleIcon } from '@heroicons/react/24/outline';

interface SubscriptionData {
  id: string;
  status: string;
  current_period_end?: string;
  current_period_start?: string;
  cancel_at_period_end?: boolean;
  plan: {
    name: string;
    amount: number;
  };
  items: any;
}

interface PlanData {
  product: {
    id: string;
    name: string;
    description?: string;
  };
  prices: {
    id: string;
    unit_amount: number;
  }[];
}

const SubscriptionTab: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const { user, refreshUser } = useAuth();
  const [subscription, setSubscription] = useState<SubscriptionData | null>(null);
  const [plans, setPlans] = useState<PlanData[]>([]);
  const [showPlansModal, setShowPlansModal] = useState(false);
  const [showPaymentMethodModal, setShowPaymentMethodModal] = useState(false);
  const [customerId, setCustomerId] = useState<string>('');

  // Copy subscription-related functions from Organization.tsx
  const fetchSubscription = useCallback(async () => {
    try {
      console.log('user', user);
      if (!user?.currentOrganization?.subscription?.id) {
        return;
      }
      const response = await stripeService.getCurrentSubscription();
      setSubscription(response);
    } catch (error) {
      console.error('Error fetching subscription:', error);
    }
  }, [user]);

  const fetchPlans = useCallback(async () => {
    try {
      const response = await stripeService.getPlans();
      setPlans(response);
    } catch (error) {
      console.error('Error fetching plans:', error);
    }
  }, []);

  // Add a new function to fetch customer info
  const fetchCustomerInfo = useCallback(async () => {
    try {
      if (!user?.currentOrganization?.subscription?.id) {
        return;
      }
      const response = await stripeService.getCurrentSubscription();
      if (response.customer && response.customer.id) {
        setCustomerId(response.customer.id);
      }
    } catch (error) {
      console.error('Error fetching customer info:', error);
    }
  }, [user]);

  useEffect(() => {
    const fetchData = async () => {
      if (user?.currentOrganization) {
        try {
          setLoading(true);
          await Promise.all([
            fetchSubscription(),
            fetchPlans(),
            fetchCustomerInfo()
          ]);
        } finally {
          setLoading(false);
        }
      }
    };
    fetchData();
  }, [fetchSubscription, fetchPlans, fetchCustomerInfo, user]);

  const handleCancelSubscription = async () => {
    if (!subscription) return;

    if (window.confirm('Are you sure you want to cancel your subscription?')) {
      try {
        setLoading(true);
        await stripeService.cancelSubscription(subscription.id);
        await fetchSubscription();
        await refreshUser();
      } catch (error) {
        console.error('Error canceling subscription:', error);
      } finally {
        setLoading(false);
      }
    }
  };

  const handlePlanChange = async (newPlan: PlanData) => {
    if (!subscription) return;

    try {
      setLoading(true);
      const currentItem = subscription.items.data[0];

      await stripeService.updateSubscription(subscription.id, {
        proration_behavior: 'always_invoice',
        items: [{
          id: currentItem.id,
          price: newPlan.prices[0].id
        }]
      });

      await fetchSubscription();
      await refreshUser();
      setShowPlansModal(false);
    } catch (error) {
      console.error('Error updating subscription:', error);
      setError('Failed to update subscription');
    } finally {
      setLoading(false);
    }
  };

  const PlansModal = () => (
    <div className={`fixed inset-0 z-50 flex items-center justify-center ${showPlansModal ? '' : 'hidden'}`}>
      <div className="fixed inset-0 bg-black opacity-50" onClick={() => setShowPlansModal(false)}></div>
      <div className="relative bg-white rounded-lg w-full max-w-4xl mx-4">
        <div className="p-6 border-b">
          <h2 className="text-2xl font-semibold">Change Subscription Plan</h2>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {[...plans].sort((a, b) => a.prices[0].unit_amount - b.prices[0].unit_amount).map((plan) => (
              <div key={plan.product.id} className={`rounded-lg shadow-md ${subscription?.plan?.name === plan.product.name ? 'bg-blue-50' : 'bg-white'}`}>
                <div className="p-6 flex flex-col h-full">
                  <div className="flex-grow">
                    <h3 className="text-xl font-semibold mb-2">{plan.product.name}</h3>
                    <div className="text-3xl text-blue-600 font-bold mb-4">
                      ${(plan.prices[0].unit_amount / 100).toFixed(2)}/month
                    </div>
                    {plan.product.description && (
                      <p className="text-gray-600 mb-4">{plan.product.description}</p>
                    )}
                  </div>
                  <button
                    className={`w-full py-2 px-4 rounded-md font-medium ${subscription?.plan?.name === plan.product.name
                      ? 'bg-gray-200 cursor-not-allowed'
                      : 'bg-blue-600 hover:bg-blue-700 text-white'
                      }`}
                    disabled={subscription?.plan?.name === plan.product.name}
                    onClick={() => handlePlanChange(plan)}
                  >
                    {subscription?.plan?.name === plan.product.name ? 'Current Plan' : 'Select Plan'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  const formatDate = (timestamp: string) => {
    console.log('timestamp', typeof timestamp);
    if (timestamp && typeof timestamp === 'string' && timestamp.includes('T')) {
      return new Date(timestamp).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    }
    return new Date(parseInt(timestamp) * 1000).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount / 100);
  };
  console.log('subscription', subscription);
  return (
    <div className="grid gap-6">
      <div>
        <h1 className="text-2xl font-semibold mb-4">Subscription Details</h1>
      </div>
      {subscription ? (
        <>
          <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <div className="text-sm text-gray-600">Subscription ID</div>
                <div className="mb-4">{subscription.id}</div>
                <div className="text-sm text-gray-600">Status</div>
                <div className="mb-4">
                  <p>{subscription.status.charAt(0).toUpperCase() + subscription.status.slice(1)}
                    {subscription.cancel_at_period_end && (
                      <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                        Canceling at Period End
                      </span>
                    )}
                  </p>
                </div>
              </div>
              <div>
                <div className="text-sm text-gray-600">Current Period Ends</div>
                <div className="mb-4">{formatDate(subscription.current_period_end)}</div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Plan</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Quantity</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {subscription.items.data.map((item: any) => (
                  <tr key={item.id}>
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-gray-900">
                        {item.price.product.name || item.plan.id}
                      </div>
                      <div className="text-sm text-gray-500">
                        Billed {item.plan.interval}ly
                      </div>
                    </td>
                    <td className="px-6 py-4 text-right text-sm text-gray-900">{item.quantity}</td>
                    <td className="px-6 py-4 text-right text-sm text-gray-900">
                      {formatCurrency(item.plan.amount)}
                    </td>
                    <td className="px-6 py-4 text-right text-sm font-medium">
                      <button
                        className="text-blue-600 hover:text-blue-900 mr-3"
                        onClick={() => setShowPlansModal(true)}
                        title="Change Plan"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                          <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                        </svg>
                      </button>
                      <button
                        className="text-red-600 hover:text-red-900"
                        onClick={handleCancelSubscription}
                        title="Cancel Subscription"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-6 flex justify-between items-center">
            <button
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={() => setShowPaymentMethodModal(true)}
              disabled={!customerId}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                <path d="M4 4a2 2 0 00-2 2v1h16V6a2 2 0 00-2-2H4z" />
                <path fillRule="evenodd" d="M18 9H2v5a2 2 0 002 2h12a2 2 0 002-2V9zM4 13a1 1 0 011-1h1a1 1 0 110 2H5a1 1 0 01-1-1zm5-1a1 1 0 100 2h1a1 1 0 100-2H9z" clipRule="evenodd" />
              </svg>
              Update Payment Method
            </button>
            <div className="flex items-center">
              <span className="text-sm font-medium text-gray-700 mr-4">Total Monthly:</span>
              <span className="text-xl font-semibold">
                {formatCurrency(
                  subscription.items.data.reduce(
                    (total: number, item: any) => total + item.plan.amount * item.quantity,
                    0
                  )
                )}
              </span>
            </div>
          </div>
        </>
      ) : (
        <div>
          <p className="mb-4">No active subscription found.</p>
          <button
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            onClick={() => navigate('/plans')}
          >
            View Plans
          </button>
        </div>
      )}
      <PlansModal />
      {customerId && (
        <UpdatePaymentMethodModal
          open={showPaymentMethodModal}
          onClose={() => setShowPaymentMethodModal(false)}
          customerId={customerId}
        />
      )}
      <LoadingOverlay loading={loading} />
    </div>
  );
};

export default SubscriptionTab; 